import React, { useState, useEffect } from 'react';
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { motion } from 'framer-motion';
import { LuCheck, LuX, LuLoader } from 'react-icons/lu';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const DistributionsChecklist = ({ userCourses = [] }) => {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [completedCourses, setCompletedCourses] = useState({});

  // Load requirements from Firestore
  useEffect(() => {
    const loadRequirements = async () => {
      if (!currentUser) return;
      
      try {
        const collegeDomain = currentUser.email.split('@')[1];
        const requirementsRef = collection(db, `colleges/${collegeDomain}/requirements`);
        const snapshot = await getDocs(requirementsRef);
        
        const requirementsData = [];
        snapshot.forEach((doc) => {
          requirementsData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Sort by category and order
        requirementsData.sort((a, b) => {
          if (a.category !== b.category) {
            return (a.category || '').localeCompare(b.category || '');
          }
          return (a.order || 0) - (b.order || 0);
        });
        
        setRequirements(requirementsData);
      } catch (err) {
        console.error('Error loading requirements:', err);
        setError('Failed to load requirements');
      } finally {
        setLoading(false);
      }
    };
    
    loadRequirements();
  }, [currentUser]);

  // Calculate completion status for each requirement
  useEffect(() => {
    if (requirements.length === 0) return;
    
    const completion = {};
    
    requirements.forEach(req => {
      // Count courses that fulfill this requirement
      const fulfillmentCount = userCourses.filter(course => {
        return checkRequirementMatch(course, req);
      }).length;
      
      completion[req.id] = {
        completed: fulfillmentCount,
        required: req.required || 1,
        isComplete: fulfillmentCount >= (req.required || 1)
      };
    });
    
    setCompletedCourses(completion);
  }, [userCourses, requirements]);

  // Helper function to check if a course matches a requirement
  const checkRequirementMatch = (course, requirement) => {
    // Check by requirement codes in course data
    if (requirement.codes && course.requirements) {
      const hasMatchingCode = requirement.codes.some(code => 
        course.requirements.includes(code)
      );
      if (hasMatchingCode) return true;
    }
    
    // Check by department codes
    if (requirement.departments && course.departmentCode) {
      const hasMatchingDept = requirement.departments.includes(course.departmentCode);
      if (hasMatchingDept) return true;
    }
    
    // Check by course codes (specific courses)
    if (requirement.courseCodes && course.courseCode) {
      const hasMatchingCourse = requirement.courseCodes.some(code => 
        course.courseCode.includes(code)
      );
      if (hasMatchingCourse) return true;
    }
    
    return false;
  };

  const getProgressColor = (req, completion) => {
    const color = req.color || 'blue';
    const colors = {
      purple: completion.isComplete ? 'bg-purple-500' : 'bg-purple-200 dark:bg-purple-900',
      blue: completion.isComplete ? 'bg-blue-500' : 'bg-blue-200 dark:bg-blue-900',
      green: completion.isComplete ? 'bg-green-500' : 'bg-green-200 dark:bg-green-900',
      orange: completion.isComplete ? 'bg-orange-500' : 'bg-orange-200 dark:bg-orange-900',
      red: completion.isComplete ? 'bg-red-500' : 'bg-red-200 dark:bg-red-900',
      pink: completion.isComplete ? 'bg-pink-500' : 'bg-pink-200 dark:bg-pink-900',
      yellow: completion.isComplete ? 'bg-yellow-500' : 'bg-yellow-200 dark:bg-yellow-900',
      indigo: completion.isComplete ? 'bg-indigo-500' : 'bg-indigo-200 dark:bg-indigo-900'
    };
    return colors[color] || colors.blue;
  };

  const getTextColor = (req, completion) => {
    const color = req.color || 'blue';
    const colors = {
      purple: completion.isComplete ? 'text-purple-700 dark:text-purple-300' : 'text-purple-600 dark:text-purple-400',
      blue: completion.isComplete ? 'text-blue-700 dark:text-blue-300' : 'text-blue-600 dark:text-blue-400',
      green: completion.isComplete ? 'text-green-700 dark:text-green-300' : 'text-green-600 dark:text-green-400',
      orange: completion.isComplete ? 'text-orange-700 dark:text-orange-300' : 'text-orange-600 dark:text-orange-400',
      red: completion.isComplete ? 'text-red-700 dark:text-red-300' : 'text-red-600 dark:text-red-400',
      pink: completion.isComplete ? 'text-pink-700 dark:text-pink-300' : 'text-pink-600 dark:text-pink-400',
      yellow: completion.isComplete ? 'text-yellow-700 dark:text-yellow-300' : 'text-yellow-600 dark:text-yellow-400',
      indigo: completion.isComplete ? 'text-indigo-700 dark:text-indigo-300' : 'text-indigo-600 dark:text-indigo-400'
    };
    return colors[color] || colors.blue;
  };

  if (loading) {
    return (
      <div className={`w-96 ${theme.glassCard} flex flex-col overflow-hidden`}>
        <div className={`border-b border-white/30 dark:border-white/20 ${theme.glassHeader} py-4 px-4`}>
          <h3 className={`font-semibold ${theme.text.primary} mb-2`}>Requirements</h3>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <LuLoader className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`w-96 ${theme.glassCard} flex flex-col overflow-hidden`}>
        <div className={`border-b border-white/30 dark:border-white/20 ${theme.glassHeader} py-4 px-4`}>
          <h3 className={`font-semibold ${theme.text.primary} mb-2`}>Requirements</h3>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className={`text-center ${theme.text.muted} text-sm`}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-96 ${theme.glassCard} flex flex-col overflow-hidden`}>
      <div className={`border-b border-white/30 dark:border-white/20 ${theme.glassHeader} py-4 px-4`}>
        <h3 className={`font-semibold ${theme.text.primary} mb-2`}>Graduation Requirements</h3>
        <p className={`text-sm ${theme.text.secondary}`}>
          Track your progress towards graduation
        </p>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {requirements.map((req) => {
            const completion = completedCourses[req.id] || { completed: 0, required: req.required || 1, isComplete: false };
            
            return (
              <motion.div
                key={req.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`${theme.glassCard} p-4 hover:shadow-md transition-all duration-200`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={`font-medium ${theme.text.primary}`}>
                        {req.name}
                      </h4>
                      {completion.isComplete ? (
                        <LuCheck className="w-4 h-4 text-green-500" />
                      ) : (
                        <LuX className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                    <p className={`text-xs ${theme.text.muted} mb-2`}>
                      {req.description}
                    </p>
                    {req.category && (
                      <span className={`inline-block text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 ${theme.text.muted} mb-2`}>
                        {req.category}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="mb-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className={`text-sm ${getTextColor(req, completion)}`}>
                      {completion.completed} of {completion.required}
                    </span>
                    <span className={`text-xs ${theme.text.muted}`}>
                      {completion.isComplete ? 'Complete' : 'In Progress'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(req, completion)}`}
                      style={{
                        width: `${Math.min((completion.completed / completion.required) * 100, 100)}%`
                      }}
                    />
                  </div>
                </div>
                
                {/* Status indicator */}
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${completion.isComplete ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <span className={`text-xs ${theme.text.secondary}`}>
                    {completion.isComplete 
                      ? 'Requirement fulfilled' 
                      : `${completion.required - completion.completed} more needed`
                    }
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
        
        {/* Overall progress summary */}
        <div className={`mt-6 ${theme.glassCard} p-4`}>
          <h4 className={`font-medium ${theme.text.primary} mb-3`}>Overall Progress</h4>
          {(() => {
            const totalComplete = Object.values(completedCourses).filter(c => c.isComplete).length;
            const totalRequired = requirements.length;
            const progressPercent = (totalComplete / totalRequired) * 100;
            
            return (
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm ${theme.text.secondary}`}>
                    {totalComplete} of {totalRequired} distributions complete
                  </span>
                  <span className={`text-sm font-medium ${theme.text.primary}`}>
                    {Math.round(progressPercent)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div
                    className="h-3 bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default DistributionsChecklist;